import { useEffect, type RefObject } from 'react';

/**
 * Keep a fixed fullscreen shell glued to the visual viewport.
 * iOS Safari otherwise scrolls/offsets the page when a soft keyboard opens,
 * which pushes fixed player chrome (and the video) off-screen.
 */
export function usePinToVisualViewport(
  rootRef: RefObject<HTMLElement | null>,
  enabled: boolean,
): void {
  useEffect(() => {
    if (!enabled) return undefined;

    const root = document.documentElement;
    const body = document.body;
    const shell = rootRef.current;
    if (!shell) return undefined;

    const prev = {
      htmlOverflow: root.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyWidth: body.style.width,
      bodyHeight: body.style.height,
      scrollX: window.scrollX,
      scrollY: window.scrollY,
    };

    root.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${prev.scrollY}px`;
    body.style.left = `-${prev.scrollX}px`;
    body.style.width = '100%';
    body.style.height = '100%';

    const sync = () => {
      window.scrollTo(0, 0);
      const vv = window.visualViewport;
      if (!vv) {
        shell.style.top = '0px';
        shell.style.left = '0px';
        shell.style.width = '100%';
        shell.style.height = '100dvh';
        return;
      }
      shell.style.top = `${vv.offsetTop}px`;
      shell.style.left = `${vv.offsetLeft}px`;
      shell.style.width = `${vv.width}px`;
      shell.style.height = `${vv.height}px`;
    };

    sync();
    const vv = window.visualViewport;
    vv?.addEventListener('resize', sync);
    vv?.addEventListener('scroll', sync);
    window.addEventListener('resize', sync);

    return () => {
      vv?.removeEventListener('resize', sync);
      vv?.removeEventListener('scroll', sync);
      window.removeEventListener('resize', sync);

      shell.style.top = '';
      shell.style.left = '';
      shell.style.width = '';
      shell.style.height = '';

      root.style.overflow = prev.htmlOverflow;
      body.style.overflow = prev.bodyOverflow;
      body.style.position = prev.bodyPosition;
      body.style.top = prev.bodyTop;
      body.style.left = prev.bodyLeft;
      body.style.width = prev.bodyWidth;
      body.style.height = prev.bodyHeight;
      window.scrollTo(prev.scrollX, prev.scrollY);
    };
  }, [enabled, rootRef]);
}
